import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { SKILLS, AVAILABLE_FOR, titleCase } from "../../lib/crewHr";
import { Loader2, Search, MapPin, Briefcase, Mail, Phone } from "lucide-react";

const fmtErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const API = process.env.REACT_APP_BACKEND_URL;

export default function TalentPool() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [skill, setSkill] = useState("");
  const [availableFor, setAvailableFor] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (skill) p.set("skill", skill);
      if (availableFor) p.set("available_for", availableFor);
      const { data } = await api.get(`/crew/talent-pool?${p.toString()}`);
      setRows(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skill, availableFor]);

  return (
    <div className="max-w-5xl px-4 md:px-0" data-testid="talent-pool-page">
      <div className="mb-2 text-xs uppercase tracking-wide text-zinc-400">Recruitment</div>
      <h1 className="font-display text-3xl mb-1">Talent pool</h1>
      <p className="text-sm text-zinc-500 mb-6">Crew who are open to work and visible for recruitment. Search by role, skill or availability.</p>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
          <Input className="pl-9" placeholder="Search name, role, nationality, location…" value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="talent-search" />
        </div>
        <select className="h-10 rounded-md border border-input px-3 text-sm" value={skill} onChange={(e) => setSkill(e.target.value)} data-testid="talent-skill">
          <option value="">Any skill</option>{SKILLS.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
        </select>
        <select className="h-10 rounded-md border border-input px-3 text-sm" value={availableFor} onChange={(e) => setAvailableFor(e.target.value)} data-testid="talent-available">
          <option value="">Any availability</option>{AVAILABLE_FOR.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 p-10"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">No crew match your search.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((c) => (
            <div key={c.user_id} className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`talent-card-${c.user_id}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-12 w-12 rounded-full bg-zinc-100 overflow-hidden flex items-center justify-center text-zinc-400">
                  {c.profile_photo ? <img alt="" className="h-full w-full object-cover" src={`${API}/api/files/${c.profile_photo}`} />
                    : (c.full_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.full_name || "Unnamed"}</div>
                  <div className="text-xs text-zinc-500 flex items-center gap-1"><Briefcase className="h-3 w-3" />{c.position || "—"}</div>
                </div>
              </div>
              <div className="text-xs text-zinc-500 space-y-1 mb-3">
                {c.nationality && <div>{c.nationality}{c.years_experience ? ` · ${c.years_experience} yrs exp` : ""}</div>}
                {c.current_location && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" />{c.current_location}</div>}
                {c.day_rate && <div>Day rate: <span className="font-medium text-zinc-700">{c.day_rate}</span></div>}
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {(c.available_for || []).map((a) => <Badge key={a} className="bg-ink/5 text-ink text-[10px]">{titleCase(a)}</Badge>)}
                {(c.skills || []).slice(0, 4).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{titleCase(s)}</Badge>)}
              </div>
              <div className="flex items-center gap-3 text-xs">
                {c.email && <a className="text-blue-600 flex items-center gap-1" href={`mailto:${c.email}`}><Mail className="h-3 w-3" />Email</a>}
                {c.phone && <a className="text-blue-600 flex items-center gap-1" href={`tel:${c.phone}`}><Phone className="h-3 w-3" />Call</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
